// MARK://dhk8/admin-shell/lib/clients/fiscal
// Ajustes fiscales (RFC, CSD, folios, régimen) → dhk8-comp-tenant-config `GET/PUT /v1/fiscal`
// (tenant resuelto del header x-dhk8-tenant-id; la ruta crea una fila por defecto en el
// primer GET, así que ya no 404ea). Los "ajustes del negocio" de UI que el backend todavía
// no persiste (horarios, encabezado/pie de recibo) se conservan de los defaults locales —
// TODO(backend): mover a tenant-config cuando exista esa parte del esquema.
// La subida del CSD va a dhk8-fiscal-cfdi. Preview: copia mutable de mock-data.
import { svcUrl, apiData } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import { AdminFiscalSettingsQuery, AdminUpdateFiscalSettingsMutation } from '../queries/fiscal.queries.js'
import * as M from '../mock-data.js'

let settings = structuredClone(M.FISCAL_SETTINGS)

export const REGIMES = [
  { code: '601', name: 'General de Ley Personas Morales' },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', name: 'Sueldos y Salarios' },
  { code: '606', name: 'Arrendamiento' },
  { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '621', name: 'Incorporación Fiscal' },
  { code: '626', name: 'Régimen Simplificado de Confianza (RESICO)' },
] as const

// ── API shape (dhk8-comp-tenant-config /v1/fiscal) ─────────────────────────
interface ApiFiscal {
  id?: string
  tenantId?: string
  legalName?: string
  rfc?: string
  regimeCode?: string
  regimeName?: string
  zipCode?: string
  csdUploaded?: boolean
  csdExpiresAt?: string | null
  folioSeries?: string
  folioNextNumber?: number
  testMode?: boolean
}

// ── adapters (API DTO ↔ UI FiscalSettings) ─────────────────────────────────
function toUi(a: ApiFiscal): M.FiscalSettings {
  // Carry over the UI-only fields (business hours / receipt template) from the last
  // known local copy until the backend persists them.
  return {
    ...settings,
    rfc: a.rfc ?? '',
    legalName: a.legalName ?? '',
    regimeCode: a.regimeCode ?? '',
    regimeName: a.regimeName ?? '',
    csdUploaded: a.csdUploaded ?? false,
    csdExpiresAt: a.csdExpiresAt ? new Date(a.csdExpiresAt).toISOString().slice(0, 10) : null,
    folioSeries: a.folioSeries ?? 'A',
    folioNext: a.folioNextNumber ?? 1,
  }
}
function toApi(s: M.FiscalSettings): ApiFiscal {
  return {
    legalName: s.legalName,
    rfc: s.rfc,
    regimeCode: s.regimeCode,
    regimeName: s.regimeName,
    csdUploaded: s.csdUploaded,
    csdExpiresAt: s.csdExpiresAt,
    folioSeries: s.folioSeries,
    folioNextNumber: s.folioNext,
  }
}

export async function getFiscalSettings(signal?: AbortSignal): Promise<M.FiscalSettings> {
  if (PREVIEW_STUB) return mock(settings)
  // F3.5 / ADR-0009: lectura por el grafo (comp-tenant-config `fiscalSettings`,
  // tenant del JWT, get-or-create). El try/catch queda como fallback defensivo.
  // Escrituras (PUT ajustes, upload CSD) siguen REST.
  try {
    const ctx = signal ? { fetchOptions: { signal } } : undefined
    const res = await gqlClient.query(AdminFiscalSettingsQuery, {}, ctx).toPromise()
    if (res.error) throw res.error
    const f = res.data?.fiscalSettings
    if (!f) return structuredClone(M.FISCAL_SETTINGS)
    settings = toUi({
      legalName: f.legalName, rfc: f.rfc, regimeCode: f.regimeCode, regimeName: f.regimeName,
      zipCode: f.zipCode, csdUploaded: f.csdUploaded, csdExpiresAt: f.csdExpiresAt,
      folioSeries: f.folioSeries, folioNextNumber: f.folioNextNumber, testMode: f.testMode,
    })
    return structuredClone(settings)
  } catch {
    return structuredClone(M.FISCAL_SETTINGS)
  }
}
export async function saveFiscalSettings(next: M.FiscalSettings): Promise<M.FiscalSettings> {
  if (PREVIEW_STUB) { settings = structuredClone(next); return mock(settings) }
  // F8.1 / ADR-0009: escritura por el grafo (comp-tenant-config `updateFiscalSettings`,
  // tenant del JWT). CSD/certificado se sube aparte (REST seguro a fiscal-cfdi).
  const a = toApi(next)
  const res = await gqlClient.mutation(AdminUpdateFiscalSettingsMutation, {
    input: {
      legalName: a.legalName, rfc: a.rfc, regimeCode: a.regimeCode, regimeName: a.regimeName,
      folioSeries: a.folioSeries, folioNextNumber: a.folioNextNumber,
    },
  }).toPromise()
  if (res.error) throw res.error
  const f = res.data?.updateFiscalSettings
  // Keep the UI-only fields the caller just edited; overlay the persisted ones.
  settings = f
    ? { ...structuredClone(next), ...toUi({
        legalName: f.legalName, rfc: f.rfc, regimeCode: f.regimeCode, regimeName: f.regimeName,
        zipCode: f.zipCode, csdUploaded: f.csdUploaded, csdExpiresAt: f.csdExpiresAt,
        folioSeries: f.folioSeries, folioNextNumber: f.folioNextNumber, testMode: f.testMode,
      }) }
    : structuredClone(next)
  return structuredClone(settings)
}
/** Read a File into base64 (no data: prefix). */
async function fileToBase64(f: File): Promise<string> {
  const buf = await f.arrayBuffer()
  // browser: btoa de un binario chunked para evitar el stack-overflow del spread.
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)))
  }
  return btoa(binary)
}

/**
 * Upload a CSD (.cer + .key + password) as real binary files.
 *
 * Antes la UI pedía nombres de archivo en inputs de texto y no subía nada
 * real — el flag se ponía a true vacío. Ahora pedimos `File` y los mandamos
 * a `dhk8-comp-tenant-config POST /v1/fiscal/csd` en base64. El backend:
 *   1. parsea el .cer (X.509) para extraer expiración + serial,
 *   2. guarda los artefactos en `tenant_fiscal_settings.csdData` (JSONB),
 *   3. marca `csdUploaded=true` + `csdExpiresAt`.
 *
 * Preview: simula el flag (no toca backend).
 */
export async function uploadCsd(cer: File, key: File, password: string): Promise<{ expiresAt: string | null; certSerialNumber?: string }> {
  if (PREVIEW_STUB) {
    settings.csdUploaded = true
    settings.csdExpiresAt = '2028-12-31'
    return mock({ expiresAt: settings.csdExpiresAt })
  }
  const [cerBase64, keyBase64] = await Promise.all([fileToBase64(cer), fileToBase64(key)])
  return apiData<{ uploaded: boolean; expiresAt: string | null; certSerialNumber?: string }>(
    svcUrl('dhk8-comp-tenant-config', '/v1/fiscal/csd'),
    { method: 'POST', body: { cerBase64, keyBase64, password, cerFileName: cer.name, keyFileName: key.name } },
  )
}
